import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ITask } from '../../types/task';
import './TaskCard.css';

interface TaskCardProps {
    task: ITask;
    onToggleStatus: (taskId: string) => void;
    onEdit?: (task: ITask) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onToggleStatus, onEdit }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return t('tasks.never');
        try {
            return new Date(dateStr).toLocaleString();
        } catch {
            return dateStr;
        }
    };

    const getStatusColor = (): string => {
        if (task.is_running) return 'running';
        return task.status === 'ACTIVE' ? 'active' : 'inactive';
    };

    return (
        <div className={`task-card ${getStatusColor()}`}>
            <div className="task-card-header">
                <div className="task-name-section">
                    <h3 className="task-name">{task.name}</h3>
                    {task.group && (
                        <span className="task-group-badge">{task.group}</span>
                    )}
                </div>
                <div className="task-status-section">
                    {task.is_running && (
                        <span className="task-running-indicator">
                            <span className="material-icons rotating">sync</span>
                            {t('tasks.running')}
                        </span>
                    )}
                    <span className={`task-status-badge ${task.status?.toLowerCase() || 'unknown'}`}>
                        {task.status || 'Unknown'}
                    </span>
                </div>
            </div>

            {task.description && (
                <p className="task-description">{task.description}</p>
            )}

            <div className="task-card-details">
                <div className="task-detail">
                    <span className="material-icons">layers</span>
                    <span>{t('tasks.pipelineOrder')}: {task.pipeline_order}</span>
                </div>
                <div className="task-detail">
                    <span className="material-icons">inventory_2</span>
                    <span>{t('tasks.batchSize')}: {task.batch_size}</span>
                </div>
                <div className="task-detail">
                    <span className="material-icons">timer</span>
                    <span>{t('tasks.waitTime')}: {task.wait_time}s</span>
                </div>
                <div className="task-detail">
                    <span className="material-icons">cloud</span>
                    <span>{task.provider || t('tasks.noProvider')}</span>
                </div>
            </div>

            {task.experiment_name && (
                <div className="task-experiment">
                    <span className="material-icons">science</span>
                    <span>{task.experiment_name}</span>
                </div>
            )}

            <div className="task-card-meta">
                <div className="task-last-run">
                    <span className="material-icons">schedule</span>
                    <span>{t('tasks.lastRun')}: {formatDate(task.last_run)}</span>
                </div>
            </div>

            <div className="task-card-actions">
                <button
                    className="task-details-btn"
                    onClick={() => navigate(`/tasks/${task._id}`)}
                    title={t('tasks.viewDetails')}
                >
                    <span className="material-icons">visibility</span>
                    {t('tasks.viewDetails')}
                </button>
                <button
                    className={`task-toggle-btn ${task.status === 'ACTIVE' ? 'deactivate' : 'activate'}`}
                    onClick={() => onToggleStatus(task._id)}
                    title={task.status === 'ACTIVE' ? t('tasks.deactivate') : t('tasks.activate')}
                >
                    <span className="material-icons">
                        {task.status === 'ACTIVE' ? 'pause_circle' : 'play_circle'}
                    </span>
                    {task.status === 'ACTIVE' ? t('tasks.deactivate') : t('tasks.activate')}
                </button>
                {onEdit && (
                    <button
                        className="task-edit-btn"
                        onClick={() => onEdit(task)}
                        title={t('tasks.edit')}
                    >
                        <span className="material-icons">edit</span>
                        {t('tasks.edit')}
                    </button>
                )}
                {task.logs_url && (
                    <a
                        href={task.logs_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="task-logs-btn"
                        title={t('tasks.viewLogs')}
                    >
                        <span className="material-icons">article</span>
                        {t('tasks.viewLogs')}
                    </a>
                )}
            </div>
        </div>
    );
};

export default TaskCard;
